import { format, subDays } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useUserStore } from '@/stores/user';

export function getDateFilterTimeZone(): string {
    const userStore = useUserStore();
    return userStore.isLoggedIn() ? userStore.timeZone : 'Europe/London';
}

function toLocalCalendarDate(year: number, month: number, day: number, hours = 0, minutes = 0, seconds = 0, ms = 0): Date {
    return new Date(year, month - 1, day, hours, minutes, seconds, ms);
}

/** Preserve calendar day from picker/state (browser-local components). */
export function normalizeCalendarDate(date: Date): Date {
    return toLocalCalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate(), 0, 0, 0, 0);
}

function calendarDayInTimeZone(refDate: Date, timeZone: string): string {
    return formatInTimeZone(refDate, timeZone, 'yyyy-MM-dd');
}

function parseCalendarDay(dayStr: string): [number, number, number] {
    const [year, month, day] = dayStr.split('-').map(Number);
    return [year, month, day];
}

/** Start of calendar day in timezone, stored as local Date for pickers/filter state. */
export function startOfDayInTimeZone(refDate: Date, timeZone: string): Date {
    const [year, month, day] = parseCalendarDay(calendarDayInTimeZone(refDate, timeZone));
    return toLocalCalendarDate(year, month, day, 0, 0, 0, 0);
}

/** End of calendar day in timezone, stored as local Date for pickers/filter state. */
export function endOfDayInTimeZone(refDate: Date, timeZone: string): Date {
    const [year, month, day] = parseCalendarDay(calendarDayInTimeZone(refDate, timeZone));
    return toLocalCalendarDate(year, month, day, 23, 59, 59, 999);
}

export function startOfDayInUserTimeZone(refDate = new Date()): Date {
    return startOfDayInTimeZone(refDate, getDateFilterTimeZone());
}

export function endOfDayInUserTimeZone(refDate = new Date()): Date {
    return endOfDayInTimeZone(refDate, getDateFilterTimeZone());
}

export function todayDateRangeInTimeZone(timeZone: string): [Date, Date] {
    const now = new Date();
    return [startOfDayInTimeZone(now, timeZone), endOfDayInTimeZone(now, timeZone)];
}

export function todayDateRangeInUserTimeZone(): [Date, Date] {
    return todayDateRangeInTimeZone(getDateFilterTimeZone());
}

export function subDaysInTimeZone(refDate: Date, days: number, timeZone: string): Date {
    const [year, month, day] = parseCalendarDay(calendarDayInTimeZone(refDate, timeZone));
    const shifted = subDays(toLocalCalendarDate(year, month, day, 12), days);
    return normalizeCalendarDate(shifted);
}

export function subDaysInUserTimeZone(refDate: Date, days: number): Date {
    return subDaysInTimeZone(refDate, days, getDateFilterTimeZone());
}

/** Format filter Date for API — calendar components are interpreted in the given timezone. */
export function formatDateFilterForApi(date: Date, timeZone: string): string {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeStr = format(date, 'HH:mm:ss');
    const instant = fromZonedTime(`${dateStr}T${timeStr}`, timeZone);
    return formatInTimeZone(instant, timeZone, "yyyy-MM-dd'T'HH:mm:ss") + ' GMT' + formatInTimeZone(instant, timeZone, 'XXX');
}
